import React from "react";
import {
    Grid,
    Typography,
} from "@material-ui/core";
import {Controller} from "react-hook-form";
import {SocItem} from "../../domain/SocItem";
import {SectionProps} from "./SectionProps";
import {Error} from "../common/Error";
import {fieldLabels} from "./fieldLabels";
import {TextField} from "@ofqual/portal-components/components/TextField";

export const DevelopAndMaintainQualifications: React.FC<SectionProps<SocItem>> = (sectionProps) => {
    const {control, disabled, errors} = sectionProps;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="body1">
                    These questions are about your internal processes and systems to determine, acquire and retain
                    relevant expertise on an ongoing basis.
                </Typography>
                <Typography variant="h6">
                    Acquiring and determining expertise
                </Typography>
                <Typography variant="body1">
                    As part of your qualification development or review processes, what processes do you have in
                    place to consider:
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="qualificationExpertiseTypeInformation"
                    as={
                        <TextField
                            label={fieldLabels.qualificationExpertiseTypeInformation}
                            readOnly={disabled}
                            data-test="qualificationExpertiseTypeInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.qualificationExpertiseTypeInformation?.type === "maxLength" && (
                    <Error data-test="qualificationExpertiseTypeInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.qualificationExpertiseTypeInformation?.type === "required" && (
                    <Error data-test="qualificationExpertiseTypeInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="qualificationExpertiseAvailabilityInformation"
                    as={
                        <TextField
                            label={fieldLabels.qualificationExpertiseAvailabilityInformation}
                            readOnly={disabled}
                            data-test="qualificationExpertiseAvailabilityInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.qualificationExpertiseAvailabilityInformation?.type === "maxLength" && (
                    <Error data-test="qualificationExpertiseAvailabilityInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.qualificationExpertiseAvailabilityInformation?.type === "required" && (
                    <Error data-test="qualificationExpertiseAvailabilityInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.qualificationExpertiseExpansionInformation}
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="qualificationExpertiseExpansionInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="qualificationExpertiseExpansionInformation-field"
                            rows={8}
                            multiline
                            required
                            style = {{ marginTop: "20px" }}
                        />
                    }
                />
                {errors.qualificationExpertiseExpansionInformation?.type === "maxLength" && (
                    <Error data-test="qualificationExpertiseExpansionInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.qualificationExpertiseExpansionInformation?.type === "required" && (
                    <Error data-test="qualificationExpertiseExpansionInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6">
                    Maintaining expertise
                </Typography>
                <Typography variant="body1">
                    Please explain your review processes for ensuring that you have sufficient resource and
                    appropriate competence on an ongoing basis, including:
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="reviewProcessExpertiseInformation"
                    as={
                        <TextField
                            label={fieldLabels.reviewProcessExpertiseInformation}
                            readOnly={disabled}
                            data-test="reviewProcessExpertiseInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.reviewProcessExpertiseInformation?.type === "maxLength" && (
                    <Error data-test="reviewProcessExpertiseInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.reviewProcessExpertiseInformation?.type === "required" && (
                    <Error data-test="reviewProcessExpertiseInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="reviewProcessMonitoringInformation"
                    as={
                        <TextField
                            label={fieldLabels.reviewProcessMonitoringInformation}
                            readOnly={disabled}
                            data-test="reviewProcessMonitoringInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.reviewProcessMonitoringInformation?.type === "maxLength" && (
                    <Error data-test="reviewProcessMonitoringInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.reviewProcessMonitoringInformation?.type === "required" && (
                    <Error data-test="reviewProcessMonitoringInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="body1">
                    <p>
                        How do you decide whether to acquire the different types of expertise in-house or to outsource
                        them? Where you decide to outsource the expertise, how do you ensure that the arrangement is
                        sustainable, and that the expertise is sufficiently embedded within your organisation?
                    </p>
                    <p>
                        For this question, we are specifically interested in <b>assessment and/or technical expertise </b> 
                        needed to develop and maintain the validity of your qualifications, including the design of any
                        assessment strategies relevant to these qualifications.
                    </p>
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="expertiseAcquisitionInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="expertiseAcquisitionInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.expertiseAcquisitionInformation?.type === "maxLength" && (
                    <Error data-test="expertiseAcquisitionInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.expertiseAcquisitionInformation?.type === "required" && (
                    <Error data-test="expertiseAcquisitionInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>
        </Grid>
    );
};
